import React, { useState, useEffect, useMemo, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import logo from "../icons/bellhoptLogoBlack.svg";
import cartIcon from "../icons/cart.svg";
import searchIcon from "../icons/search-icon-black.svg";
import leftArrow from "../icons/leftArrow.svg";
import PRODUCT_CATEGORIES from "../constants/ProductCategories";

import { getProducts } from "../store/thunks/guestActions";
import {
  setActivePage,
  setBarOff,
  setBlurOff,
  updateTerm,
} from "../store/slices/guestSlices";

import SaveCartButton from "./SaveCartButton";
import {
  detectGuestPartner,
  getGuestShopHomePath,
  getStoredGuestPartner,
} from "../constants/guestPartnerPaths";
import { trackLiveSearchTakeover, trackSearch } from "../utils/analytics";
import {
  getProductSearchQuery,
  goGuestStorefrontHome,
  isInPlaceProductSearchPath,
  openProductSearch,
} from "../utils/productSearchNavigation";
import {
  LIVE_SEARCH_MIN_CHARS,
} from "../utils/localProductSearch";

const normalizeSearchTerm = (value) => {
  const raw = String(value || "");
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.split("%20").join(" ").trim();
  }
};

const getSuggestionProductName = (product) =>
  String(
    product?.description ||
      product?.name ||
      product?.title ||
      product?.items?.[0]?.description ||
      ""
  ).trim();

const MOBILE_SEARCH_SUGGESTION_LIMIT = 3;
const DESKTOP_SEARCH_SUGGESTION_LIMIT = 8;

const GuestNav = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const guestBlur = useSelector((state) => state.guestBlur);
  const { cartItems } = useSelector((state) => state.cart);
  const activeCompanyFromStore = useSelector(
    (state) => state.activeCompany.activeCompany
  );
  const { property } = useSelector((state) => state.guestLocationId || {});
  const submittedSearchTerm = useSelector((state) => state.term?.term);
  const loadedProducts = useSelector((state) => state.products?.products);

  const [search, setSearch] = useState(() => {
    const reduxTerm =
      submittedSearchTerm && submittedSearchTerm !== "Food"
        ? submittedSearchTerm
        : "";
    const storedTerm =
      typeof window !== "undefined"
        ? localStorage.getItem("lastSearchTerm")
        : "";
    return normalizeSearchTerm(reduxTerm || storedTerm);
  });
  const [additionalLogoUrl, setAdditionalLogoUrl] = useState(null);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(null);
  const [isCompressed, setIsCompressed] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const liveSearchTimerRef = useRef(null);

  // Resolve active company (store or localStorage)
  let companyData = activeCompanyFromStore;
  if (!companyData || !companyData.name) {
    try {
      const stored = localStorage.getItem("activeCompany");
      if (stored) companyData = JSON.parse(stored);
    } catch {
      // ignore
    }
  }

  const companyName = companyData?.name;
  const cartItemCount =
    companyName && cartItems[companyName]
      ? cartItems[companyName].length
      : 0;

  // Cart animation
  const [cartAnimation, setCartAnimation] = useState(false);
  const [prevCartCount, setPrevCartCount] = useState(cartItemCount);

  useEffect(() => {
    if (cartItemCount > prevCartCount) {
      setCartAnimation(true);
      const t = setTimeout(() => setCartAnimation(false), 400);
      return () => clearTimeout(t);
    }
    setPrevCartCount(cartItemCount);
  }, [cartItemCount, prevCartCount]);

  // Add-on mode hides SaveCart
  const isAddOnMode =
    typeof window !== "undefined" &&
    localStorage.getItem("addonMode") === "true";

  const isCheckout = location.pathname?.includes("/checkout");
  const isDesktop =
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false;
  const hideNav = isCheckout && !isDesktop;
  const hideSaveCart = isCheckout;

  const shopHomePath = getGuestShopHomePath();

  // Fetch host logo for partner paths, otherwise property-specific logo.
  useEffect(() => {
    let mounted = true;
    let attempts = 0;

    const logoApiBase =
      process.env.REACT_APP_PRODUCTION_BASE_URL || "https://api.bellhopt.com/api/v1";

    const getLocalPropertyId = () => {
      try {
        return JSON.parse(localStorage.getItem("property") || "{}")?._id;
      } catch {
        return null;
      }
    };

    const applyLogoUrl = (logoUrl) => {
      if (mounted) setAdditionalLogoUrl(logoUrl || null);
    };

    const fetchLogoByHost = async (hostId, fallbackLogoUrl = null) => {
      try {
        const res = await axios.get(`${logoApiBase}/logo-by-host/${hostId}`);
        applyLogoUrl(res?.data?.logoUrl || fallbackLogoUrl);
      } catch (err) {
        applyLogoUrl(fallbackLogoUrl);
        console.error("Error fetching partner host logo:", err);
      }
    };

    const fetchLogoByProperty = async (propertyId) => {
      try {
        const res = await axios.get(`${logoApiBase}/logo-by-property/${propertyId}`);
        applyLogoUrl(res?.data?.logoUrl);
      } catch (err) {
        applyLogoUrl(null);
        console.error("Error fetching additional logo:", err);
      }
    };

    const tryFetch = () => {
      if (!mounted) return;

      const partner =
        getStoredGuestPartner() || detectGuestPartner(location.pathname);
      if (partner?.hostId) {
        fetchLogoByHost(partner.hostId, partner.logoUrl);
        return;
      }
      if (partner?.logoUrl) {
        applyLogoUrl(partner.logoUrl);
        return;
      }

      const propertyId = property?._id || getLocalPropertyId();
      if (!propertyId) {
        attempts += 1;
        if (attempts <= 5) {
          setTimeout(tryFetch, 400);
        } else {
          setAdditionalLogoUrl(null);
        }
        return;
      }

      fetchLogoByProperty(propertyId);
    };

    tryFetch();

    return () => {
      mounted = false;
    };
  }, [property?._id, location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateCompression = () => {
      setIsCompressed(window.scrollY > 24);
    };

    updateCompression();
    window.addEventListener("scroll", updateCompression, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateCompression);
    };
  }, []);

  useEffect(() => {
    const queryTerm = getProductSearchQuery(location.search);
    const submitted = normalizeSearchTerm(submittedSearchTerm);
    const nextTerm =
      queryTerm ||
      (submitted && submitted !== "Food" ? submitted : "");
    if (!nextTerm) return;
    if (
      location.pathname !== "/homeproducts" &&
      !queryTerm
    ) {
      return;
    }
    setSearch(nextTerm);
  }, [location.pathname, location.search, submittedSearchTerm]);

  useEffect(() => {
    return () => {
      if (liveSearchTimerRef.current) {
        window.clearTimeout(liveSearchTimerRef.current);
      }
    };
  }, []);

  const runSearch = (termInput, source = "navigation") => {
    const trimmed = termInput.trim();
    if (!trimmed) return;

    const searchTerm = encodeURIComponent(trimmed);
    const inPlace = isInPlaceProductSearchPath(location.pathname);

    setSearch(trimmed);
    setSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
    localStorage.setItem("lastSearchTerm", trimmed);
    dispatch(updateTerm(trimmed));
    dispatch(setActivePage({ page: 1 }));
    trackSearch({ source, term: trimmed });

    // In-place search: results view owns debounced/cached catalog fetch while typing.
    // Explicit submit still kicks an immediate fetch (session cache prevents duplicates).
    if (!inPlace || source !== "live") {
      dispatch(getProducts({ term: searchTerm, limit: 48, start: 0 }));
    }

    openProductSearch(navigate, location, trimmed, {
      replace: source === "live",
    });

    if (props.func) {
      props.func(10);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (liveSearchTimerRef.current) {
      window.clearTimeout(liveSearchTimerRef.current);
    }
    runSearch(search, "manual");
  };

  const handleCategorySelect = (name) => {
    if (liveSearchTimerRef.current) {
      window.clearTimeout(liveSearchTimerRef.current);
    }
    runSearch(name, "category");
    setMobileCategoriesOpen(false);
    setFilteredCategories(null);
  };

  const handleEnterKey = (e, suggestions = []) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestionsOpen(true);
      setActiveSuggestionIndex((current) =>
        suggestions.length
          ? Math.min(current + 1, suggestions.length - 1)
          : -1
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((current) => Math.max(current - 1, -1));
      return;
    }

    if (e.key === "Escape") {
      setSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (liveSearchTimerRef.current) {
        window.clearTimeout(liveSearchTimerRef.current);
      }
      const selected = suggestions[activeSuggestionIndex];
      runSearch(selected || search, selected ? "suggestion" : "manual");
    }
  };

  const mobileCategories = useMemo(() => {
    const company = activeCompanyFromStore?.name || companyData?.name;
    const categories =
      PRODUCT_CATEGORIES.find((c) => c.companyName === company)?.categories ||
      [];
    return categories;
  }, [activeCompanyFromStore, companyData]);

  // Prefer curated category/search terms (catalog order = top terms), then products.
  const rankedSearchSuggestions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];

    const matchesQuery = (value) =>
      String(value || "").toLowerCase().includes(query);

    const rankByPrefix = (a, b) => {
      const aStarts = a.toLowerCase().startsWith(query);
      const bStarts = b.toLowerCase().startsWith(query);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return 0;
    };

    // Keep PRODUCT_CATEGORIES order so the first hits are the top search terms.
    const categoryMatches = mobileCategories
      .map((category) => String(category || "").trim())
      .filter((category) => category && matchesQuery(category));

    const categorySet = new Set(
      categoryMatches.map((category) => category.toLowerCase())
    );

    const productMatches = Array.from(
      new Set(
        (Array.isArray(loadedProducts) ? loadedProducts : [])
          .map(getSuggestionProductName)
          .map((name) => name.trim())
          .filter((name) => name && matchesQuery(name))
      )
    )
      .filter((name) => !categorySet.has(name.toLowerCase()))
      .sort((a, b) => {
        const byPrefix = rankByPrefix(a, b);
        if (byPrefix !== 0) return byPrefix;
        // Shorter names read more like search terms than long product titles.
        if (a.length !== b.length) return a.length - b.length;
        return a.localeCompare(b);
      });

    const prioritizedCategories = [...categoryMatches].sort((a, b) => {
      const byPrefix = rankByPrefix(a, b);
      if (byPrefix !== 0) return byPrefix;
      // Stable: preserve original catalog order among equal prefix rank.
      return categoryMatches.indexOf(a) - categoryMatches.indexOf(b);
    });

    return [...prioritizedCategories, ...productMatches];
  }, [loadedProducts, mobileCategories, search]);

  const mobileSearchSuggestions = useMemo(
    () => rankedSearchSuggestions.slice(0, MOBILE_SEARCH_SUGGESTION_LIMIT),
    [rankedSearchSuggestions]
  );

  const desktopSearchSuggestions = useMemo(
    () => rankedSearchSuggestions.slice(0, DESKTOP_SEARCH_SUGGESTION_LIMIT),
    [rankedSearchSuggestions]
  );
  const handleGoHome = (e) => {
    if (e) e.preventDefault();
    if (liveSearchTimerRef.current) {
      window.clearTimeout(liveSearchTimerRef.current);
    }
    goGuestStorefrontHome(navigate, location, {
      clearSearchInput: () => {
        setSearch("");
        setSuggestionsOpen(false);
        setActiveSuggestionIndex(-1);
      },
      updateTerm: (term) => dispatch(updateTerm(term)),
    });
  };

  const handleSearchInput = (value) => {
    const previousTrimmedLength = search.trim().length;
    setSearch(value);
    setActiveSuggestionIndex(-1);
    const trimmed = value.trim();
    setSuggestionsOpen(Boolean(trimmed));

    if (!isInPlaceProductSearchPath(location.pathname)) return;

    if (liveSearchTimerRef.current) {
      window.clearTimeout(liveSearchTimerRef.current);
    }

    if (!trimmed) {
      if (getProductSearchQuery(location.search)) {
        goGuestStorefrontHome(navigate, location, {
          clearSearchInput: () => {},
          updateTerm: (term) => dispatch(updateTerm(term)),
        });
      }
      return;
    }

    if (trimmed.length < LIVE_SEARCH_MIN_CHARS) return;

    if (previousTrimmedLength < LIVE_SEARCH_MIN_CHARS) {
      trackLiveSearchTakeover({ termLength: trimmed.length });
    }

    // Instant local results UI; catalog API is debounced inside the results view.
    dispatch(updateTerm(trimmed));
    openProductSearch(navigate, location, trimmed);
  };

  const handleSuggestionSelect = (suggestion) => {
    runSearch(suggestion, "suggestion");
  };

  const SearchSuggestions = ({ id, suggestions = [] }) => {
    if (!suggestionsOpen || suggestions.length === 0) return null;

    return (
      <div
        id={id}
        role="listbox"
        className="
          absolute left-0 right-0 top-full z-[120] mt-2
          max-h-[300px] overflow-y-auto rounded-xl
          border border-[#ddd] bg-white py-1.5
          shadow-[0_14px_34px_rgba(15,23,42,0.14)]
        "
      >
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion}
            id={`${id.replace("-suggestions", "-suggestion")}-${index}`}
            type="button"
            role="option"
            aria-selected={index === activeSuggestionIndex}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleSuggestionSelect(suggestion)}
            className={`
              block w-full px-4 py-2.5 text-left text-[13px] text-[#1a1a1a]
              transition-colors hover:bg-[#f7f7f7]
              ${index === activeSuggestionIndex ? "bg-[#f7f7f7]" : ""}
            `}
          >
            {suggestion}
          </button>
        ))}
      </div>
    );
  };

  const closeSearchSuggestions = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
  };

  const closeOverlays = () => {
    dispatch(setBlurOff());
    dispatch(setBarOff());
  };

  /* ---------- Liquid Glass Property Logo ---------- */

  const PropertyLogoGlass = ({ url, variant, compressed = false }) => {
    if (!url) return null;

    const isDesktop = variant === "desktop";
    const maxHeightClass = isDesktop
      ? compressed
        ? "max-h-[38px]"
        : "max-h-[44px]"
      : "max-h-[34px]";
    const maxWidthClass = isDesktop ? "max-w-[188px]" : "max-w-[158px]";

    return (
      <img
        src={url}
        alt="Property logo"
        className={`
          ${maxHeightClass} ${maxWidthClass}
            object-contain
          w-full
          h-full
          block
        `}
        />
    );
  };

  /* ----------------------- RENDER ----------------------- */

  if (hideNav) return null;

  return (
    <>
      {/* Captures outside taps so suggestions close without clicking products underneath */}
      {suggestionsOpen &&
        (mobileSearchSuggestions.length > 0 ||
          desktopSearchSuggestions.length > 0) && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[70]"
          onPointerDown={closeSearchSuggestions}
        />
      )}

      {/* Global blur overlay */}
      {guestBlur && (
        <div
          onClick={closeOverlays}
          className="fixed inset-0 z-10 bg-black/40 min-h-[650px]"
        />
      )}

      {/* MOBILE HEADER */}
      <header className="tablet:block laptop:hidden w-full bg-white guest-nav-mobile">
        <div
          className="
            w-full
            px-3 pt-2.5 pb-3
            flex flex-col gap-2.5
            fixed top-0 left-0 z-[80]
            bg-white/95
            backdrop-blur-xl
            shadow-sm
          "
        >
          {/* Top row: logos + save cart */}
          <div className="flex items-center justify-between gap-3">
            <a
              href={shopHomePath}
              onClick={handleGoHome}
            className="flex items-center gap-3 min-w-0 max-w-[260px] overflow-hidden"
            >
              {/* Bellhopt logo - original size */}
              <img
                src={logo}
                alt="Bellhopt"
              className="h-[36px] w-auto block"
              />

              {/* Property logo in glass pill, same visual footprint as original */}
              {additionalLogoUrl && (
                <PropertyLogoGlass
                  url={additionalLogoUrl}
                  variant="mobile"
                />
              )}
            </a>

            <div className="flex items-center gap-2">
              {!isAddOnMode && !hideSaveCart && <SaveCartButton size="sm" />}

              {/* (Optional) Mobile cart pill currently hidden */}
              <NavLink
                to="/cart"
                className={`
                  hidden
                  flex items-center gap-1
                  px-3 py-1.5
                  rounded-full
                  text-white
                  text-[11px] font-semibold
                  transition-all duration-400 ease-out
                  ${
                    cartAnimation
                      ? "bg-[#FF4D4D] shadow-[0_0_16px_rgba(255,77,77,0.4)] scale-[1.08]"
                      : "bg-[#FF4D4D] shadow-sm"
                  }
                `}
              >
                <img
                  src={cartIcon}
                  alt=""
                  className={`
                    w-[16px] h-[16px]
                    transition-all duration-400 ease-out
                    ${cartAnimation ? "scale-110 -rotate-3" : ""}
                  `}
                />
                <span
                  className={`
                    transition-all duration-400
                    ${cartAnimation ? "font-bold" : ""}
                  `}
                >
                  {cartItemCount}
                </span>
              </NavLink>
            </div>
          </div>

          {/* Second row: burger / back / centered search */}
          <div className="flex items-center gap-2 justify-between">
            {/* Optional back icon */}
            {props.back ? (
              <button
                onClick={() => navigate(-1)}
                className="
                  flex items-center justify-center
                  w-[38px] h-[38px]
                  rounded-full
                  border border-[#E5E7EB]
                  bg-white
                "
                aria-label="Go back"
              >
                <img
                  src={leftArrow}
                  alt=""
                  className="w-4 h-4"
                />
              </button>
            ) : (
              <div className="w-[38px] h-[38px]" />
            )}

            {/* Search (centered) */}
            <div className="relative flex-1 flex justify-center">
              <div className="relative w-full max-w-[420px]">
                <span className="absolute inset-y-0 left-3 flex items-center">
                  <img
                    src={searchIcon}
                    alt=""
                    className="w-4 h-4 opacity-80"
                  />
                </span>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyDown={(e) => handleEnterKey(e, mobileSearchSuggestions)}
                  onFocus={() => setSuggestionsOpen(Boolean(search.trim()))}
                  onBlur={() => setSuggestionsOpen(false)}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="mobile-search-suggestions"
                  aria-expanded={
                    suggestionsOpen && mobileSearchSuggestions.length > 0
                  }
                  aria-activedescendant={
                    activeSuggestionIndex >= 0
                      ? `mobile-search-suggestion-${activeSuggestionIndex}`
                      : undefined
                  }
                  className="
                    w-full h-[38px]
                    rounded-full
                    pl-9 pr-3
                    text-[12px]
                    bg-white/98
                    border border-[#E5E7EB]
                    text-[#111827]
                    outline-none
                    text-center
                    placeholder:text-center placeholder:text-[#9CA3AF]
                  "
                />
                <SearchSuggestions
                  id="mobile-search-suggestions"
                  suggestions={mobileSearchSuggestions}
                />              </div>
            </div>

            {/* Categories toggle */}
            <button
              type="button"
              onClick={() => setMobileCategoriesOpen((v) => !v)}
              className="
                flex items-center justify-center
                w-[38px] h-[38px]
                rounded-full
                bg-white
                border border-[#E5E7EB]
                shadow-[0_4px_12px_rgba(15,23,42,0.08)]
                active:scale-[0.96]
                transition-all
              "
              aria-label="Browse categories"
            >
              <span className="sr-only">Categories</span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111827"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          </div>
        </div>

        {/* Spacer so page content isn't hidden behind fixed header */}
        <div className="h-[88px] guest-nav-mobile-spacer" />
      </header>

      {/* Mobile categories dropdown */}
      {mobileCategoriesOpen && (
        <div
          className="tablet:block laptop:hidden fixed inset-0 z-[95]"
          onClick={() => {
            setMobileCategoriesOpen(false);
            setFilteredCategories(null);
          }}
        >
          <div
            className="absolute top-[108px] left-0 right-0 px-3 pb-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="
                rounded-2xl
                bg-white
                border border-[#E5E7EB]
                shadow-[0_14px_34px_rgba(15,23,42,0.14)]
                overflow-hidden
              "
            >
              <div className="px-3 py-2 border-b border-[#E5E7EB]/80 flex items-center justify-between sticky top-0 bg-white z-10">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                  Shop by category
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMobileCategoriesOpen(false);
                    setFilteredCategories(null);
                  }}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#111827] px-2 py-1 rounded-full border border-[#E5E7EB] hover:border-[#FF4D4D] hover:text-[#FF4D4D] transition-colors"
                  aria-label="Close categories"
                >
                  Close ✕
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto px-3 py-3 bg-white">
                <div className="grid grid-cols-2 gap-2">
                  {(filteredCategories || mobileCategories).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => handleCategorySelect(cat)}
                      className="
                        w-full text-left
                        rounded-xl
                        px-3 py-2.5
                        text-[12px] font-medium
                        text-[#111827]
                        bg-white
                        border border-[#E5E7EB]
                        shadow-[0_4px_12px_rgba(15,23,42,0.06)]
                        hover:border-[#FF4D4D]/60
                        hover:shadow-[0_6px_16px_rgba(15,23,42,0.08)]
                        transition-all
                      "
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP HEADER */}
      <header
        className={`
          hidden laptop:block
          fixed top-0 left-0
          w-full
          bg-white/92
          backdrop-blur-2xl
          z-[100]
          transition-[height,box-shadow,background-color] duration-200
          ${isCompressed ? "h-[62px] shadow-[0_6px_16px_rgba(15,23,42,0.05)]" : "h-[72px] shadow-[0_6px_18px_rgba(15,23,42,0.06)]"}
        `}
      >
        <div className="mx-auto flex h-full w-[92%] max-w-6xl items-center justify-between gap-5">
          {/* Brand + property logo */}
          <a
            href={shopHomePath}
            onClick={handleGoHome}
            className="flex items-center gap-4 min-w-0 max-w-[320px] overflow-hidden"
          >
            {/* Bellhopt logo - original size */}
            <img
              src={logo}
              alt="Bellhopt"
              className={isCompressed ? "h-[40px] w-auto" : "h-[44px] w-auto"}
            />

            {/* Property logo glass pill - original height footprint */}
            {additionalLogoUrl && (
              <PropertyLogoGlass
                url={additionalLogoUrl}
                variant="desktop"
                compressed={isCompressed}
              />
            )}
          </a>

          {/* Search */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-[560px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <img
                  src={searchIcon}
                  alt=""
                  className="w-4 h-4 opacity-75"
                />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={(e) => handleEnterKey(e, desktopSearchSuggestions)}
                onFocus={() => setSuggestionsOpen(Boolean(search.trim()))}
                onBlur={() => setSuggestionsOpen(false)}
                role="combobox"
                aria-autocomplete="list"
                aria-controls="desktop-search-suggestions"
                aria-expanded={
                  suggestionsOpen && desktopSearchSuggestions.length > 0
                }
                aria-activedescendant={
                  activeSuggestionIndex >= 0
                    ? `desktop-search-suggestion-${activeSuggestionIndex}`
                    : undefined
                }
                placeholder="Search"
                className={`
                  w-full
                  ${isCompressed ? "h-[36px] text-[12px]" : "h-[40px] text-[13px]"}
                  pl-9 pr-16
                  rounded-full
                  bg-white/98
                  border border-[#E5E7EB]
                  text-[#111827]
                  outline-none
                  placeholder:text-[#9CA3AF]
                `}
              />
              <button
                onClick={handleSearch}
                className={`
                  absolute right-2
                  ${isCompressed ? "inset-y-[4px] px-3.5 text-[10px]" : "inset-y-[6px] px-4 text-[11px]"}
                  rounded-full
                  bg-[#FF4D4D]
                  text-white font-semibold
                  flex items-center justify-center
                  shadow-[0_6px_16px_rgba(248,113,113,0.28)]
                  hover:bg-[#E63F3F]
                  transition-colors
                `}
              >
                Search
              </button>
              <SearchSuggestions
                id="desktop-search-suggestions"
                suggestions={desktopSearchSuggestions}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {!isAddOnMode && !hideSaveCart && <SaveCartButton size={isCompressed ? "sm" : "md"} />}

            <NavLink
              to="/cart"
              className={`
                flex items-center gap-2
                ${isCompressed ? "px-3.5 py-1.5 text-[11px]" : "px-4 py-2 text-[12px]"}
                rounded-full
                text-white
                font-semibold
                transition-all duration-400 ease-out
                ${
                  cartAnimation
                    ? "bg-[#FF4D4D] shadow-[0_0_20px_rgba(255,77,77,0.26)] scale-[1.05]"
                    : "bg-[#FF4D4D] shadow-[0_8px_18px_rgba(248,113,113,0.18)]"
                }
              `}
            >
              <img
                src={cartIcon}
                alt=""
                className={`
                  w-[18px] h-[18px]
                  transition-all duration-400 ease-out
                  ${cartAnimation ? "scale-110 -rotate-3" : ""}
                `}
              />
              <span
                className={`
                  transition-all duration-400
                  ${cartAnimation ? "font-bold" : ""}
                `}
              >
                {cartItemCount} item
                {cartItemCount !== 1 && "s"}
              </span>
            </NavLink>
          </div>
        </div>
      </header>

      {/* Spacer for desktop so content isn't behind fixed nav */}
      <div className="hidden laptop:block h-[72px]" />
    </>
  );
};

export default GuestNav;
